import React from 'react';
import PropTypes from 'prop-types';
import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import cn from 'classnames';
import { formatNumber } from 'helper';

const CELL =
  'h-7 min-w-[28px] px-2 inline-flex items-center justify-center rounded text-xs font-semibold tabular-nums transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-1';

function Pagination({ value, onChange, withPage }) {
  const handlePrev = () => {
    const newPage = value.current_page - 1;
    if (newPage <= 0) return;
    onChange((state) => ({ ...state, page: newPage }));
  };

  const handleNext = () => {
    const newPage = value.current_page + 1;
    if (newPage > value.last_page) return;
    onChange((state) => ({ ...state, page: newPage }));
  };

  const isFirst = +value?.current_page <= 1;
  const isLast = +value?.current_page >= +value?.last_page;

  // eslint-disable-next-line
  if (+value?.last_page === 1) return null;

  return (
    <div className="flex items-center justify-between">
      <div className="flex w-full items-center justify-between">
        {withPage && (
          <div>
            <p className="text-xs text-neutral-500 tabular-nums">
              <span className="font-semibold text-neutral-700">
                {formatNumber(value?.last_page || '1', 0)}
              </span>{' '}
              page(s)
            </p>
          </div>
        )}
        <div>
          <nav
            className="relative z-0 inline-flex gap-1"
            aria-label="Pagination"
          >
            <button
              type="button"
              onClick={handlePrev}
              disabled={isFirst}
              className={cn(
                CELL,
                isFirst
                  ? 'border border-neutral-200 text-neutral-400 cursor-not-allowed'
                  : 'border border-neutral-300 text-neutral-700 hover:bg-neutral-100',
              )}
            >
              <span className="sr-only">Previous page</span>
              <CaretLeft size={14} weight="bold" aria-hidden="true" />
            </button>
            <button
              type="button"
              disabled
              className={cn(CELL, 'bg-brand-600 text-white')}
            >
              <span>{formatNumber(value?.current_page || '1', 0)}</span>
            </button>
            <button
              type="button"
              onClick={handleNext}
              disabled={isLast}
              className={cn(
                CELL,
                isLast
                  ? 'border border-neutral-200 text-neutral-400 cursor-not-allowed'
                  : 'border border-neutral-300 text-neutral-700 hover:bg-neutral-100',
              )}
            >
              <span className="sr-only">Next page</span>
              <CaretRight size={14} weight="bold" aria-hidden="true" />
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
}

Pagination.propTypes = {
  value: PropTypes.instanceOf(Object),
  onChange: PropTypes.func,
  withPage: PropTypes.bool,
};

Pagination.defaultProps = {
  onChange: () => {},
  value: {
    current_page: 1,
    last_page: 1,
    total: 1,
  },
  withPage: true,
};

export default Pagination;
